import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { ApplicationError } from '../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { ajaxRetry } from '../../lib-api/ajaxRetry';
import { base, createApiURL, ResponseObj } from '../../lib-api/services/base';
import { ProviderConsent, ProviderConsents } from '../../models/provider';
import { CommonError } from '../../stores/app-state-errors';

export const ERR_FAILED_TO_EXTEND_CONSENT: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_EXTEND_CONSENT,
};

export const FAILED_FETCH_PROVIDER_CONSENT: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.UNEXPECTED_ERROR,
};

export interface ProviderConsentService {
  extendConsent: (credentialsId: string) => Promise<void>;
  getConsent: () => Promise<ProviderConsent[]>;
}

export interface ProviderConsentClient {
  extendConsent: (credentialsId: string) => Promise<ResponseObj<void>>;
  getConsent: () => Promise<ResponseObj<ProviderConsents | undefined>>;
}

export const providerConsentClient: ProviderConsentClient = {
  extendConsent: (credentialsId) => {
    return ajaxRetry<void>({
      headers: base.headers(),
      data: JSON.stringify({ credentialsId }),
      method: 'POST',
      url: createApiURL('/api/v1/provider-consents:extend'),
    });
  },
  getConsent() {
    return ajaxRetry({
      headers: base.headers(),
      method: 'GET',
      url: createApiURL('/api/v1/provider-consents'),
    });
  },
};

export const createProviderConsentService = (
  client: ProviderConsentClient
): ProviderConsentService => ({
  extendConsent: async (credentialsId) => {
    const response = await client.extendConsent(credentialsId);

    if (!response.isOk()) {
      throw new Error();
    }

    return undefined;
  },
  getConsent: async () => {
    const response = await client.getConsent();

    if (!response.isOk() || !response.data) {
      throw new ApplicationError(FAILED_FETCH_PROVIDER_CONSENT);
    }

    return response.data.providerConsents;
  },
});

export const providerConsentService = createProviderConsentService(providerConsentClient);
